import { Button, Form, Input, Modal, message } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
	troubleshootingGuidesRestCrudThunksForBuyer,
	troubleshootingGuidesRestCrudThunksForSupplier,
} from '../../thunks/troubleshooting_guides_thunks';
import { withUserType } from '../withUserType';
import TroubleshootingAssociationEditFields from './troubleshooting/TroubleshootingAssociationEditFields';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

const FORM_NAME = 'new-guide-form';

const TroubleshootingGuideNewOrEditModal: FC<any> = ({
	onCancel,
	onSuccess,
	createGuide,
	updateGuide,
	formData = {},
}) => {
	const [form] = Form.useForm();

	const [saving, setSaving] = useState(false);

	const isUpdate = useMemo(() => !!nullSafeGet('id', formData), [formData]);

	const createGuideAction = useCallback(
		(values) => {
			setSaving(true);
			createGuide(values)
				.then((guide) => {
					message.success(`Guide created successfully!`);
					onSuccess && onSuccess(guide);
					onCancel();
				})
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[createGuide, onCancel, onSuccess]
	);

	const updateGuideAction = useCallback(
		(values) => {
			setSaving(true);
			updateGuide(formData.id, values)
				.then((guide) => {
					message.success(`Guide updated successfully!`);
					onSuccess && onSuccess(guide);
					onCancel();
				})
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[formData.id, onCancel, onSuccess, updateGuide]
	);

	const onSubmit = useCallback(
		(values) => {
			const action = isUpdate ? updateGuideAction : createGuideAction;
			action(values);
		},
		[createGuideAction, isUpdate, updateGuideAction]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={`${isUpdate ? 'Update' : 'New'} Guide`}
			onCancel={onCancel}
			closable
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					{isUpdate ? 'Save' : 'Next'}
				</Button>,
			]}
		>
			<Form
				form={form}
				id={FORM_NAME}
				layout="vertical"
				requiredMark
				initialValues={{
					...formData,
					problemTypeIds: [],
					locationIds: [],
					workOrderPriorityIds: [],
				}}
				onFinish={onSubmit}
			>
				<Form.Item
					name="name"
					label="Guide Name"
					rules={[
						{
							required: true,
							message: 'Please give your guide a name.',
						},
					]}
				>
					<Input style={{ width: '100%' }} />
				</Form.Item>
				<Form.Item name="description" label="Description">
					<Input style={{ width: '100%' }} />
				</Form.Item>
				{isUpdate ? null : (
					<TroubleshootingAssociationEditFields form={form} displaySelectAllWithLabel />
				)}
			</Form>
		</Modal>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	createGuide: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? troubleshootingGuidesRestCrudThunksForSupplier.create(entity)
				: troubleshootingGuidesRestCrudThunksForBuyer.create(entity)
		),
	updateGuide: (id, entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? troubleshootingGuidesRestCrudThunksForSupplier.patch(id, entity)
				: troubleshootingGuidesRestCrudThunksForBuyer.patch(id, entity)
		),
});

export default withUserType(
	connect(null, mapDispatchToProps)(TroubleshootingGuideNewOrEditModal as any) as any
);
